'use client'

import { createContext, useContext, useEffect, useState } from 'react'
import type { PagePerformanceLifecycle } from '@/lib/page-performance/PagePerformanceLifecycle'

export const PagePerformanceLifecycleContext =
  createContext<PagePerformanceLifecycle | null>(null)

export const usePagePerformanceLifecycle =
  (): PagePerformanceLifecycle | null =>
    useContext(PagePerformanceLifecycleContext)

type LifecycleSubscribe = (listener: () => void) => () => void

const useLifecyclePhase = (
  lifecycle: PagePerformanceLifecycle | null,
  subscribe: LifecycleSubscribe | undefined
): boolean => {
  const [reachedLifecycle, setReachedLifecycle] =
    useState<PagePerformanceLifecycle | null>(null)

  useEffect(() => {
    if (!lifecycle || !subscribe) return
    return subscribe(() => {
      setReachedLifecycle(lifecycle)
    })
  }, [lifecycle, subscribe])

  return lifecycle !== null && reachedLifecycle === lifecycle
}

export const usePageVisible = (): boolean => {
  const lifecycle = usePagePerformanceLifecycle()
  return useLifecyclePhase(lifecycle, lifecycle?.subscribeVisible)
}

export const usePageInteractive = (): boolean => {
  const lifecycle = usePagePerformanceLifecycle()
  return useLifecyclePhase(lifecycle, lifecycle?.subscribeInteractive)
}
